import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

import {
  CREATE_GET_USER_GROUPS,
  CREATE_USER_GROUP_LIST_API,
  DELETE,
  DELETE_USER_FROM_GROUP_API,
  DELETE_USER_FROM_GROUPS,
  DELETE_USER_GROUP_API,
  DELETE_USER_GROUPS,
  DELETE_USER_PLAN_BY_IDS,
  GET,
  GET_USER_GROUPS_LEARNING_PLAN_BY_ID,
  GET_AD_GROUP_LEARNING_PLAN_BY_ID_API,
  GET_LEARNING_PLAN_LIST,
  GET_LEARNING_PLAN_LIST_API,
  GET_USER_GROUP_BY_ID_API,
  GET_USER_GROUP_LIST_API,
  GET_USER_GROUPS_BY_ID,
  GET_USER_GROUPS_BY_ID_BY_TAB,
  GET_USER_GROUPS_BY_ID_BY_USER_ID,
  GET_USER_GROUPS_LIST,
  POST,
  PUT,
  UPDATE_GET_USER_GROUPS,
  UPDATE_USER_GROUP_LIST_API,
  UPDATE_USER_INDUVIDUAL_PLAN,
  UPDATE_USER_INDUVIDUAL_PLAN_API,
} from "../../Api";
import apiRequest from "../../Api/connector";
import { STATUS_200 } from "../../Api/constants";
import { LearningPlanDetails } from "../../Api/entities/LearningPlanMasterEntity";
import {
  CreateUserGroup,
  LearningPlanResponse,
  UserGroupsResponse,
} from "../../Api/entities/UserGroupEntity";
import { SomethingWentWrong } from "../../utils/Constant";
import { showToast } from "../../utils/helper/helper";

interface UserGroupInterface {
  userGroups: UserGroupsResponse | any;
  learningPlans: LearningPlanResponse;
  userGroupById: CreateUserGroup | any;
  addUserGroupLearningPlanById: LearningPlanDetails | any;
  userGroupByIdByTab: any;
  userGroupByIdByUserId: any;
  loading: boolean;
  error: string | null;
}

const initialState: UserGroupInterface = {
  addUserGroupLearningPlanById: {},
  userGroupByIdByUserId: {},
  learningPlans: {
    learning_plan: [],
  },
  userGroupById: {},
  userGroupByIdByTab: {},
  userGroups: {},
  loading: false,
  error: null,
};

export const getUserGroupList = createAsyncThunk(
  GET_USER_GROUPS_LIST,
  async () => {
    const response = await apiRequest(GET, GET_USER_GROUP_LIST_API());
    return response?.data;
  }
);

export const getLearningPlanList = createAsyncThunk(
  GET_LEARNING_PLAN_LIST,
  async () => {
    const response = await apiRequest(GET, GET_LEARNING_PLAN_LIST_API());
    return response;
  }
);

export const getUserGroupById = createAsyncThunk(
  GET_USER_GROUPS_BY_ID,
  async ({ id }: { id: string }) => {
    const response = await apiRequest(
      GET,
      GET_USER_GROUP_BY_ID_API(id, null, null)
    );
    return response?.data;
  }
);

export const getAddGroupLearningPlanById = createAsyncThunk(
  GET_USER_GROUPS_LEARNING_PLAN_BY_ID,
  async ({
    id,
    domain,
    role,
    level,
  }: {
    id: string;
    domain?: string;
    role?: string;
    level?: string;
  }) => {
    const response = await apiRequest(
      GET,
      GET_AD_GROUP_LEARNING_PLAN_BY_ID_API(id, domain, role, level)
    );
    return response?.data;
  }
);

export const getUserGroupByIdByTab = createAsyncThunk(
  GET_USER_GROUPS_BY_ID_BY_TAB,
  async ({ id, tab }: { id: string; tab: string }) => {
    const response = await apiRequest(
      GET,
      GET_USER_GROUP_BY_ID_API(id, tab, null)
    );
    return response?.data;
  }
);

export const getUserGroupByIdByUserId = createAsyncThunk(
  GET_USER_GROUPS_BY_ID_BY_USER_ID,
  async ({ id, user_id }: { id: string; user_id: string }) => {
    const response = await apiRequest(
      GET,
      GET_USER_GROUP_BY_ID_API(id, null, user_id)
    );
    return response?.data;
  }
);

export const createUserGroup = createAsyncThunk(
  CREATE_GET_USER_GROUPS,
  async ({ payload }: { payload: CreateUserGroup }) => {
    const response = await apiRequest(
      POST,
      CREATE_USER_GROUP_LIST_API(),
      payload
    );
    return response;
  }
);

export const updateUserGroup = createAsyncThunk(
  UPDATE_GET_USER_GROUPS,
  async ({ payload, type }: { payload: CreateUserGroup; type?: string }) => {
    const response = await apiRequest(
      PUT,
      UPDATE_USER_GROUP_LIST_API(type),
      payload
    );
    return response;
  }
);

export const updateUserInduvidualPlan = createAsyncThunk(
  UPDATE_USER_INDUVIDUAL_PLAN,
  async ({ payload }: { payload: any }) => {
    const response = await apiRequest(
      PUT,
      UPDATE_USER_INDUVIDUAL_PLAN_API(),
      payload
    );
    return response;
  }
);

export const deleteUserGroup = createAsyncThunk(
  DELETE_USER_GROUPS,
  async ({ id }: { id: any }) => {
    const response = await apiRequest(DELETE, DELETE_USER_GROUP_API(id));
    return response;
  }
);

export const deleteUserFromGroup = createAsyncThunk(
  DELETE_USER_FROM_GROUPS,
  async ({ payload }: { payload: any }) => {
    const response = await apiRequest(
      DELETE,
      DELETE_USER_FROM_GROUP_API(),
      payload
    );
    return response;
  }
);

export const deleteUserPlanByIds = createAsyncThunk(
  DELETE_USER_PLAN_BY_IDS,
  async ({
    id,
    user_id,
    payload,
  }: {
    id: string;
    user_id: string;
    payload: any;
  }) => {
    const response = await apiRequest(
      DELETE,
      GET_USER_GROUP_BY_ID_API(id, null, user_id),
      payload
    );
    return response;
  }
);

const UserGroupSlice = createSlice({
  name: "userGroup",
  reducers: {},
  initialState,
  extraReducers: builder => {
    builder
      .addCase(getUserGroupList.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getUserGroupList.fulfilled,
        (state, action: PayloadAction<UserGroupsResponse>) => {
          state.loading = false;
          state.userGroups = action.payload;
        }
      )
      .addCase(getUserGroupList.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getUserGroupById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getUserGroupById.fulfilled,
        (state, action: PayloadAction<CreateUserGroup>) => {
          state.loading = false;
          state.userGroupById = action.payload;
        }
      )
      .addCase(getUserGroupById.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getAddGroupLearningPlanById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getAddGroupLearningPlanById.fulfilled,
        (state, action: PayloadAction<LearningPlanDetails>) => {
          state.loading = false;
          state.addUserGroupLearningPlanById = action.payload;
        }
      )
      .addCase(getAddGroupLearningPlanById.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getUserGroupByIdByTab.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserGroupByIdByTab.fulfilled, (state, action) => {
        state.loading = false;
        state.userGroupByIdByTab = action.payload;
      })
      .addCase(getUserGroupByIdByTab.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getUserGroupByIdByUserId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserGroupByIdByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.userGroupByIdByUserId = action.payload;
      })
      .addCase(getUserGroupByIdByUserId.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getLearningPlanList.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getLearningPlanList.fulfilled,
        (state, action: PayloadAction<LearningPlanResponse>) => {
          state.loading = false;
          state.learningPlans = action.payload;
        }
      )
      .addCase(getLearningPlanList.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(createUserGroup.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUserGroup.fulfilled, (state, action) => {
        state.loading = false;

        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(createUserGroup.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(updateUserGroup.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserGroup.fulfilled, (state, action) => {
        state.loading = false;

        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(updateUserGroup.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(updateUserInduvidualPlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserInduvidualPlan.fulfilled, (state, action) => {
        state.loading = false;

        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(updateUserInduvidualPlan.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(deleteUserGroup.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserGroup.fulfilled, (state, action) => {
        state.loading = false;

        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(deleteUserGroup.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(deleteUserFromGroup.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserFromGroup.fulfilled, (state, action) => {
        state.loading = false;

        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(deleteUserFromGroup.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });
  },
});

export default UserGroupSlice.reducer;
